import { Injectable } from "@angular/core";
import { PhoneNumber } from "../model/PhoneNumber";

@Injectable({
    providedIn: 'root'
})
export class UIHelpers {

    static PHONE_PATTERN = "(111) 111-1111"


    constructor() {
    }

    // This is used when allowing for a slight margin on an offset,
    // such as when scrolling to the top of an element.
    static ADDITIONAL_MARGIN = -20;

    public demagnifyAmount(magnifiedAmount:number, magnifier:number): number {
        if (magnifiedAmount && magnifier) {
            return (magnifiedAmount / magnifier);
        }
        else {
            return 0;
        }
    }

    public magnifyAmount(amount:number, magnifier:number): number {
        if (amount && magnifier) {
            return Math.round(amount * magnifier);
        }
        else {
            return 0;
        }
    }

    public formatCurrencyToString(amount: number): string {
        return UIHelpers.formatCurrency(amount);
    }

    public static scrollToTop(selector:string|undefined = undefined): Promise<void> {
        return UIHelpers.smoothScroll(selector, UIHelpers.getHeaderOffset());
    }

    // Only scroll if the top of the element is out of view
    public static scrollToTopIfOffPage(selector:string|undefined, provideAdditionalMargin:boolean = true) {

        let element = undefined;
        if (selector) {
            element = document.getElementById(selector);
        }
        if (selector && element) {
            let y = element.getBoundingClientRect().top;
            if (y + (UIHelpers.getHeaderOffset()) < 0) {
                let additionalOffset = (provideAdditionalMargin ? UIHelpers.ADDITIONAL_MARGIN : 0);
                UIHelpers.smoothScroll(selector, UIHelpers.getHeaderOffset() + additionalOffset)
            }
        }
        else {
            UIHelpers.smoothScroll("", 0)
        }
    }

    private static getHeaderOffset():number {
        let siteHeader = document.getElementById("siteHeader");
        if (siteHeader && siteHeader.clientHeight) {
            return (siteHeader.clientHeight + 20) * -1;
        }
        else {
            return 0;
        }
    }

    // Completes the scroll within the context of a promise
    private static smoothScroll(selector:string|undefined, offset = 0): Promise<void> {

        if (!selector) {
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
              });
      
            return new Promise((resolve, reject) => {
                resolve();
            });
        }


        const element = document.getElementById(selector);

        if (!element) {
            throw "element [" + selector + "] not found";
        }

        const rect = element.getBoundingClientRect();
        let targetPosition = Math.floor(rect.top + self.scrollY + offset);
        window.scrollTo({
          top: targetPosition,
          behavior: 'smooth'
        });

        return new Promise((resolve, reject) => {
          //const failed = setTimeout(() => {
          //  reject();
          //}, 2000);
      
          const scrollHandler = () => {
            if (Math.floor(self.scrollY) === Math.floor(targetPosition)) {
              window.removeEventListener("scroll", scrollHandler);
              //clearTimeout(failed);
              resolve();
            }
          };
          if (Math.floor(self.scrollY) === Math.floor(targetPosition)) {
            //clearTimeout(failed);
            resolve();
          } else {
            window.addEventListener("scroll", scrollHandler);
            element.getBoundingClientRect();
          }
        });
      }

    public getCardName(first4:string):string {
        let visaRegEx = new RegExp(/^4[\d]{3}/);
        let mastercardRegEx = new RegExp(/^5[\d]{3}/);
        let discoverRegEx = new RegExp(/^6[\d]{3}/);
        let amexRegEx = new RegExp(/^(34|37)[\d]{2}/);

        if (first4.match(visaRegEx)) {
            return "Visa";
        }
        else if (first4.match(mastercardRegEx)) {
            return "Mastercard";
        }
        else if (first4.match(discoverRegEx)) {
            return "Discover";
        }
        else if (first4.match(amexRegEx)) {
            return "American Express";
        }
        else {
            throw "unknown card type";
        }
    }

    // TODO: This will eventually need some localization...
    // This intentionally does not take a pattern option in order
    // to maintain site-wide consistency
    public static formatPhone(p:string, ccCode:string) {
        if (!ccCode || ccCode == "") {
            ccCode = "US";
        }

        if (!p) {
            return "";
        } 

        // strip anything that isn't a digit (dashes, parens, spaces, dots, etc.)
        p = p.replace(/[^0-9]/g, "");
        //if (UIHelpers.PHONE_PATTERN.replace(/\D/g, "").length != p.replace(/\D/g, "").length) {
        if (!PhoneNumber.validate(p, ccCode)) {
            throw "provided phone number does not match pattern " + UIHelpers.PHONE_PATTERN;
        }        

        let formatted = "";
        let pIdx = 0;
        for (let i=0; i<UIHelpers.PHONE_PATTERN.length; i++) {
            if (UIHelpers.PHONE_PATTERN[i] == "1") {
                formatted += p[pIdx];
                ++pIdx;
            }
            else {
                formatted += UIHelpers.PHONE_PATTERN[i];
            }
        }
        return formatted;
    }

    public static formatDate(date:string) {
        let dt:Date = new Date(date);
        let shortDate = dt.toLocaleDateString('en-us', { month:"short", year:"numeric", day: "2-digit", hour:"2-digit", minute:"2-digit", timeZone: "CST", timeZoneName:"short"});
        return shortDate
    }

    public static formatCurrency(amt:any): string {
        const formatter = new Intl.NumberFormat('en-US', {
            style: 'currency',
            currency: 'USD',
        });

        if (amt == undefined || amt == null || isNaN(amt)) {
            return formatter.format(0);
        }
        return formatter.format(amt);
    }

    public formatDateTime(date:string, timeOnNewLine:boolean|false) {

        let separator = " ";

        if (timeOnNewLine) {
            separator = "<br/>";
        }

        let dt:Date = new Date(date);

        //let time_12h = dt.toLocaleTimeString('en-us');
        let timeWithZone = dt.toLocaleTimeString('en-us',{timeZoneName:'short'});
        //let timezoneShort = dt.toLocaleTimeString('en-us',{timeZoneName:'short'}).split(' ')[2]
        //console.log(time_12h + " (" + timezoneShort + ")");
        let shortDate = dt.toLocaleDateString('en-us', { month:"short", year:"numeric", day: "2-digit"});

        return shortDate + separator + timeWithZone;
      
    }

    // zero-based month number (ie 0=January)
    public static getMonthAbbrFromNumber(monthNum:any|undefined): string {
        if (monthNum) {
            return ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"][parseInt(monthNum)-1]
        }
        return "";
    }

    public calculateDaysSince(date1:Date, date2:Date): number {
        let startDate = new Date(date1);
        let endDate = new Date(date2);

        let difference =  Math.floor((Date.UTC(endDate.getFullYear(), endDate.getMonth(), endDate.getDate()) - Date.UTC(startDate.getFullYear(), startDate.getMonth(), startDate.getDate()) ) /(1000 * 60 * 60 * 24));

        if (difference < 0) {
            return 0;
        }
        return difference;
    }


}
